/**
 * Backend FIRM COMPLIANCE QUEUE API — admin queue hook.
 *
 * Wraps GET /v1/admin/risk-review. Visible to
 * admin/super_admin/compliance/rm. Items without a riskScore surface as
 * "Unrated" — the backend sorts those to the top so the admin sees them
 * first.
 *
 * TWO SCREENS, ONE BOOK READ (2026-08-22). This response backs BOTH
 * `/admin/risk-review` (AML) and `/admin/fica` (FICA s24A periodic review) —
 * hence the FICA fields alongside the risk ones. `/admin/fica` used to derive
 * itself from `useTrusts()`, the OWN-TENANT trustee list, which under
 * first-class-per-client tenancy is empty for every real firm; it then declared
 * "every trust is current under FICA s24A" about a book it had never read.
 * Reading one endpoint means the two queues can never disagree about the same
 * book.
 *
 * For a b2b firm the backend aggregates across the firm's grant-verified client
 * tenants, so every row carries the CLIENT that owns it, and `clientsSkipped` /
 * `truncated` say when the queue is partial. A partial compliance queue must
 * announce itself — "unknown" is a legitimate answer, "clear" is not.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from './client';

export interface RiskReviewItemView {
  id: string;
  tenantId: string;
  trustName: string;
  registration: string;
  /** null = the firm's OWN tenant; otherwise the client tenant holding it. */
  clientTenantId: string | null;
  clientDisplayName: string | null;
  /** FICA s24A review date, or null when the trust carries none. */
  nextFicaReview: string | null;
  /** Days until that review; NEGATIVE means overdue. Derived server-side. */
  ficaDaysOut: number;
  /** TRS-03: whether an initial FICA exists on the register (`false` = people
   *  recorded, none verified; `null` = unknown). Absent from an older backend. */
  initialFicaOnFile?: boolean | null;
  lastUpdated: string | null;
  riskScore?: number;
  riskScoredAt?: string;
  riskReviewedAt?: string;
  nextRiskReviewAt?: string;
  riskReviewedByUserId?: string;
}

export interface RiskReviewListResponse {
  items: RiskReviewItemView[];
  /** Client tenants dropped by a read failure this request. 0 when healthy. */
  clientsSkipped: number;
  /** The firm's book exceeded the fan-out cap; the queue is a subset. */
  truncated: boolean;
  correlationId: string;
}

/** Exported for the admin-dashboard prefetch — same key, same fetcher. */
export const ADMIN_RISK_REVIEW_QUERY_KEY = ['admin', 'risk-review'] as const;

export function fetchAdminRiskReview(signal?: AbortSignal) {
  return api<RiskReviewListResponse>('/api/v1/admin/risk-review', { signal });
}

export function useAdminRiskReview(opts: { enabled?: boolean } = {}) {
  return useQuery<RiskReviewListResponse>({
    queryKey: ADMIN_RISK_REVIEW_QUERY_KEY,
    queryFn: ({ signal }) => fetchAdminRiskReview(signal),
    // Role-gated callers pass enabled:false instead of firing a doomed 403
    // (director is outside the backend's worklist tier — 2026-07-19
    // miss-hunt P1 state-code-firm-01).
    enabled: opts.enabled !== false,
  });
}
